// notificationService/index.js - 通知服务主接口

import { isMock } from '../../utils/envCheck.js';

import { localStorageAdapter } from './localStorage.js';
import { supabaseAdapter } from './supabase.js';

/**
 * 通知服务接口
 * 提供统一的通知数据访问，支持多数据源适配
 */
export const notificationService = {
  /**
   * 创建关注通知
   * @param {string} followerId - 关注者ID
   * @param {string} followerName - 关注者姓名
   * @param {string} followedId - 被关注者ID
   * @returns {Promise<Object>} 创建结果
   */
  createFollowNotification: async (followerId, followerName, followedId) => {
    try {
      const notification = {
        id: crypto.randomUUID(),
        type: 'follow',
        senderId: followerId,
        senderName: followerName,
        receiverId: followedId,
        title: 'New Follower',
        content: `${followerName} followed you`,
        message: `${followerName} followed you`,
        isRead: false,
        createdAt: new Date().toISOString(),
        meta: {
          action: 'follow',
          followerId,
          followerName,
        },
      };

      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created follow notification in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase (但主要依赖数据库触发器)
        const supabaseResult = await supabaseAdapter.createNotification(
          notification
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Created follow notification in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created follow notification in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to create follow notification`
      );
      return { success: false, error: 'Failed to create follow notification' };
    } catch (error) {
      console.error(
        `[notificationService] Error creating follow notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 创建点赞通知
   * @param {string} likerId - 点赞者ID
   * @param {string} likerName - 点赞者姓名
   * @param {string} workId - 作品ID
   * @param {string} workTitle - 作品标题
   * @param {string} workAuthorId - 作品作者ID
   * @returns {Promise<Object>} 创建结果
   */
  createLikeNotification: async (
    likerId,
    likerName,
    workId,
    workTitle,
    workAuthorId
  ) => {
    try {
      const notification = {
        id: crypto.randomUUID(),
        type: 'like',
        senderId: likerId,
        senderName: likerName,
        receiverId: workAuthorId,
        title: 'Work Liked',
        content: `${likerName} liked your work "${workTitle}"`,
        message: `${likerName} liked your work "${workTitle}"`,
        isRead: false,
        createdAt: new Date().toISOString(),
        meta: {
          action: 'like',
          workId,
          workTitle,
          likerId,
          likerName,
        },
      };

      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created like notification in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase (但主要依赖数据库触发器)
        const supabaseResult = await supabaseAdapter.createNotification(
          notification
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Created like notification in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created like notification in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(`[notificationService] Failed to create like notification`);
      return { success: false, error: 'Failed to create like notification' };
    } catch (error) {
      console.error(
        `[notificationService] Error creating like notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 创建评论通知
   * @param {string} commenterId - 评论者ID
   * @param {string} commenterName - 评论者姓名
   * @param {string} workId - 作品ID
   * @param {string} workTitle - 作品标题
   * @param {string} workAuthorId - 作品作者ID
   * @param {string} commentContent - 评论内容
   * @returns {Promise<Object>} 创建结果
   */
  createCommentNotification: async (
    commenterId,
    commenterName,
    workId,
    workTitle,
    workAuthorId,
    commentContent
  ) => {
    try {
      const notification = {
        id: crypto.randomUUID(),
        type: 'comment',
        senderId: commenterId,
        senderName: commenterName,
        receiverId: workAuthorId,
        title: 'New Comment',
        content: `${commenterName} commented on your work "${workTitle}"`,
        message: `${commenterName} commented on your work "${workTitle}": "${commentContent}"`,
        isRead: false,
        createdAt: new Date().toISOString(),
        meta: {
          action: 'comment',
          workId,
          workTitle,
          commenterId,
          commenterName,
          commentContent,
        },
      };

      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created comment notification in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase (但主要依赖数据库触发器)
        const supabaseResult = await supabaseAdapter.createNotification(
          notification
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Created comment notification in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created comment notification in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to create comment notification`
      );
      return { success: false, error: 'Failed to create comment notification' };
    } catch (error) {
      console.error(
        `[notificationService] Error creating comment notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 获取用户通知列表
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 通知列表
   */
  getUserNotifications: async userId => {
    try {
      // 根据环境变量决定使用哪种存储方式
      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const localStorageNotifications =
          await localStorageAdapter.getUserNotifications(userId);
        if (localStorageNotifications && localStorageNotifications.success) {
          console.log(
            `[notificationService] Using LocalStorage notifications for user ${userId}`
          );
          return localStorageNotifications;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseNotifications =
          await supabaseAdapter.getUserNotifications(userId);
        if (supabaseNotifications && supabaseNotifications.success) {
          console.log(
            `[notificationService] Using Supabase notifications for user ${userId}`
          );
          return supabaseNotifications;
        }

        // Supabase失败后，fallback到LocalStorage
        console.warn(
          `[notificationService] Supabase failed, falling back to LocalStorage`
        );
        const localStorageNotifications =
          await localStorageAdapter.getUserNotifications(userId);
        if (localStorageNotifications && localStorageNotifications.success) {
          console.log(
            `[notificationService] Using LocalStorage notifications (fallback) for user ${userId}`
          );
          return localStorageNotifications;
        }
      }

      console.log(
        `[notificationService] No notifications found for user ${userId}`
      );
      return { success: true, data: [] };
    } catch (error) {
      console.error(
        `[notificationService] Error getting notifications for user ${userId}:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 创建Collaboration申请通知
   * @param {string} applicantId - 申请者ID
   * @param {string} applicantName - 申请者姓名
   * @param {string} applicantAvatar - 申请者头像
   * @param {string} collaborationId - 协作项目ID
   * @param {string} collaborationTitle - 协作项目标题
   * @param {string} initiatorId - 项目发起者ID
   * @param {Object} applicationData - 申请数据
   * @returns {Promise<Object>} 创建结果
   */
  createCollaborationApplicationNotification: async (
    applicantId,
    applicantName,
    applicantAvatar,
    collaborationId,
    collaborationTitle,
    initiatorId,
    applicationData
  ) => {
    try {
      // 📥 收到参数
      console.log(
        '[notificationService.createCollaborationApplicationNotification] 📥 收到参数:'
      );
      console.log(
        '  - applicantId:',
        applicantId,
        '(type:',
        typeof applicantId,
        ')'
      );
      console.log(
        '  - applicantName:',
        applicantName,
        '(type:',
        typeof applicantName,
        ')'
      );
      console.log(
        '  - applicantAvatar:',
        applicantAvatar,
        '(type:',
        typeof applicantAvatar,
        ')'
      );
      console.log(
        '  - collaborationId:',
        collaborationId,
        '(type:',
        typeof collaborationId,
        ')'
      );
      console.log(
        '  - collaborationTitle:',
        collaborationTitle,
        '(type:',
        typeof collaborationTitle,
        ')'
      );
      console.log(
        '  - initiatorId:',
        initiatorId,
        '(type:',
        typeof initiatorId,
        ')'
      );
      console.log(
        '  - applicationData:',
        applicationData,
        '(type:',
        typeof applicationData,
        ')'
      );

      // ❌ 参数验证
      if (!applicantId) {
        console.error('❌❌❌ CRITICAL: applicantId是null或undefined！');
      }
      if (
        !applicantName ||
        applicantName === '' ||
        applicantName === 'Anonymous'
      ) {
        console.error('❌❌❌ CRITICAL: applicantName无效:', applicantName);
      }
      if (!collaborationId) {
        console.error('❌❌❌ CRITICAL: collaborationId是null或undefined！');
      }
      if (!initiatorId) {
        console.error('❌❌❌ CRITICAL: initiatorId是null或undefined！');
      }

      const notification = {
        id: `collaboration_application_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        type: 'collaboration',
        senderId: applicantId,
        senderName: applicantName,
        senderAvatar: applicantAvatar,
        receiverId: initiatorId,
        title: 'New Collaboration Application',
        content: `${applicantName} applied for your collaboration "${collaborationTitle}"`,
        message: `${applicantName} applied for your collaboration "${collaborationTitle}". Click to view the application details.`,
        isRead: false,
        createdAt: new Date().toISOString(),
        meta: {
          action: 'collaboration_application',
          collaborationId,
          collaborationTitle,
          applicantId,
          applicantName,
          applicantAvatar,
          applicationData,
        },
      };

      // 📦 构建的notification对象
      console.log(
        '[notificationService.createCollaborationApplicationNotification] 📦 构建的notification对象:'
      );
      console.log(
        '  - senderId:',
        notification.senderId,
        '(type:',
        typeof notification.senderId,
        ')'
      );
      console.log(
        '  - senderName:',
        notification.senderName,
        '(type:',
        typeof notification.senderName,
        ')'
      );
      console.log(
        '  - senderAvatar:',
        notification.senderAvatar,
        '(type:',
        typeof notification.senderAvatar,
        ')'
      );
      console.log(
        '  - receiverId:',
        notification.receiverId,
        '(type:',
        typeof notification.receiverId,
        ')'
      );
      console.log('  - meta:', notification.meta);
      console.log(
        '  - meta.applicantId:',
        notification.meta.applicantId,
        '(type:',
        typeof notification.meta.applicantId,
        ')'
      );
      console.log(
        '  - meta.applicantName:',
        notification.meta.applicantName,
        '(type:',
        typeof notification.meta.applicantName,
        ')'
      );
      console.log('  - 完整对象:', JSON.stringify(notification, null, 2));

      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created collaboration application notification in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseResult = await supabaseAdapter.createNotification(
          notification
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Created collaboration application notification in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created collaboration application notification in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to create collaboration application notification`
      );
      return {
        success: false,
        error: 'Failed to create collaboration application notification',
      };
    } catch (error) {
      console.error(
        `[notificationService] Error creating collaboration application notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 标记通知为已读
   * @param {string} notificationId - 通知ID
   * @returns {Promise<Object>} 更新结果
   */
  markAsRead: async notificationId => {
    try {
      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.markAsRead(notificationId);
        if (result.success) {
          console.log(
            `[notificationService] Marked notification ${notificationId} as read in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseResult = await supabaseAdapter.markAsRead(notificationId);
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Marked notification ${notificationId} as read in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.markAsRead(notificationId);
        if (result.success) {
          console.log(
            `[notificationService] Marked notification ${notificationId} as read in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to mark notification ${notificationId} as read`
      );
      return { success: false, error: 'Failed to mark notification as read' };
    } catch (error) {
      console.error(
        `[notificationService] Error marking notification as read:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 标记所有通知为已读
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 更新结果
   */
  markAllAsRead: async userId => {
    try {
      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.markAllAsRead(userId);
        if (result.success) {
          console.log(
            `[notificationService] Marked all notifications as read in LocalStorage for user ${userId}`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseResult = await supabaseAdapter.markAllAsRead(userId);
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Marked all notifications as read in Supabase for user ${userId}`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.markAllAsRead(userId);
        if (result.success) {
          console.log(
            `[notificationService] Marked all notifications as read in LocalStorage (fallback) for user ${userId}`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to mark all notifications as read for user ${userId}`
      );
      return {
        success: false,
        error: 'Failed to mark all notifications as read',
      };
    } catch (error) {
      console.error(
        `[notificationService] Error marking all notifications as read:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 删除通知
   * @param {string} notificationId - 通知ID
   * @returns {Promise<Object>} 删除结果
   */
  deleteNotification: async notificationId => {
    try {
      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.deleteNotification(
          notificationId
        );
        if (result.success) {
          console.log(
            `[notificationService] Deleted notification ${notificationId} in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseResult = await supabaseAdapter.deleteNotification(
          notificationId
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Deleted notification ${notificationId} in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.deleteNotification(
          notificationId
        );
        if (result.success) {
          console.log(
            `[notificationService] Deleted notification ${notificationId} in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to delete notification ${notificationId}`
      );
      return { success: false, error: 'Failed to delete notification' };
    } catch (error) {
      console.error(
        `[notificationService] Error deleting notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },

  /**
   * 创建通用通知
   * @param {Object} params - 通知参数
   * @param {string} params.userId - 接收者ID
   * @param {string} params.type - 通知类型
   * @param {string} params.title - 通知标题
   * @param {string} params.message - 通知消息
   * @param {string} params.projectId - 项目ID（可选）
   * @param {Object} params.meta - 元数据（可选）
   * @returns {Promise<Object>} 创建结果
   */
  createGeneralNotification: async params => {
    try {
      const notification = {
        id: `general_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: params.type || 'general',
        senderId: null, // 通用通知没有发送者
        senderName: null,
        receiverId: params.userId,
        title: params.title,
        content: params.message,
        message: params.message,
        isRead: false,
        createdAt: new Date().toISOString(),
        meta: {
          action: params.type,
          projectId: params.projectId,
          ...params.meta,
        },
      };

      if (isMock()) {
        // Mock模式 - 使用LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created general notification in LocalStorage`
          );
          return result;
        }
      } else {
        // 生产模式 - 使用Supabase
        const supabaseResult = await supabaseAdapter.createNotification(
          notification
        );
        if (supabaseResult.success) {
          console.log(
            `[notificationService] Created general notification in Supabase`
          );
          return supabaseResult;
        }

        // Supabase失败后，fallback到LocalStorage
        const result = await localStorageAdapter.createNotification(
          notification
        );
        if (result.success) {
          console.log(
            `[notificationService] Created general notification in LocalStorage (fallback)`
          );
          return result;
        }
      }

      console.warn(
        `[notificationService] Failed to create general notification`
      );
      return { success: false, error: 'Failed to create general notification' };
    } catch (error) {
      console.error(
        `[notificationService] Error creating general notification:`,
        error
      );
      return { success: false, error: error.message };
    }
  },
};

export default notificationService;
